import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// Point pdf.js worker to a CDN that matches the pdfjs-dist version we use
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

/**
 * PdfCanvas — renders a PDF (or image fallback) and renders children on top
 * in an absolutely-positioned overlay sized to match the rendered page.
 *
 * Props:
 *   fileUrl: string (data URL or URL)
 *   pageNumber: number (1-indexed)
 *   onLoadPages: (numPages) => void
 *   renderOverlay: ({ width, height, pageNumber }) => ReactNode
 *   maxWidth: number (px) — upper bound; actual render width is min(maxWidth, container width)
 */
export default function PdfCanvas({
  fileUrl,
  pageNumber = 1,
  onLoadPages,
  renderOverlay,
  maxWidth = 820,
  onPageClick,
}) {
  const [numPages, setNumPages] = useState(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [containerWidth, setContainerWidth] = useState(0);
  const wrapperRef = useRef(null);

  // Track outer wrapper width so the page never exceeds the viewport on mobile
  useEffect(() => {
    if (!wrapperRef.current || typeof ResizeObserver === "undefined") return;
    const el = wrapperRef.current;
    const update = () => setContainerWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setDims({ width: 0, height: 0 });
  }, [pageNumber, fileUrl]);

  if (!fileUrl) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-white h-96 flex items-center justify-center text-sm text-slate-500">
        No document loaded
      </div>
    );
  }

  // Use the smaller of the container width and the requested maxWidth so the
  // PDF always fits its parent column (especially on mobile screens).
  const renderWidth = containerWidth > 0
    ? Math.min(maxWidth, Math.floor(containerWidth))
    : maxWidth;

  return (
    <div ref={wrapperRef} className="w-full flex justify-center">
      <div
        className="relative inline-block max-w-full"
        style={{ maxWidth: renderWidth }}
        onClick={(e) => onPageClick && onPageClick(e, dims)}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            onLoadPages && onLoadPages(n);
          }}
          loading={
            <div className="h-96 flex items-center justify-center text-sm text-slate-400">
              Loading document…
            </div>
          }
          error={
            <div className="h-96 flex items-center justify-center text-sm text-red-500">
              Failed to load document.
            </div>
          }
        >
          <Page
            pageNumber={Math.min(pageNumber, numPages || pageNumber)}
            width={renderWidth}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onRenderSuccess={({ width, height }) => setDims({ width, height })}
          />
        </Document>
        {dims.width > 0 && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ width: dims.width, height: dims.height }}
          >
            <div
              className="absolute inset-0 pointer-events-auto"
              style={{
                width: maxWidth,
                height: dims.height * (maxWidth / dims.width),
                transform: `scale(${dims.width / maxWidth})`,
                transformOrigin: "top left",
              }}
            >
              {renderOverlay && renderOverlay({ width: maxWidth, height: dims.height * (maxWidth / dims.width), pageNumber })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
